import { useEffect, useState } from "react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import { listBudgets, getBudget, getActuals } from "@/utils/budgetApi";

const eur = (n) =>
  new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(n ?? 0);

const MONTH_NAMES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
function monthLabel(mo) {
  if (!mo) return "";
  const m = parseInt(mo.slice(5, 7), 10);
  return `${MONTH_NAMES[m - 1] ?? mo} ${mo.slice(2, 4)}`;
}

const SCENARIOS = [
  { value: "optimistic", label: "Optimista" },
  { value: "medium", label: "Medio" },
  { value: "pessimistic", label: "Pesimista" },
];

// Mes actual en formato YYYY-MM (los meses posteriores aún no tienen "real").
function currentYM() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

// Desvío en color: para el RESULTADO y la CAJA, más es mejor → verde si real ≥ presupuesto.
function devTone(real, bud) {
  if (real == null || bud == null) return "text-slate-400";
  const d = real - bud;
  if (Math.abs(d) < 0.005) return "text-slate-500";
  return d > 0 ? "text-green-700" : "text-red-700";
}

// Comparación Presupuesto vs Real (lado a lado, mes a mes).
// No toca el backend: cruza el presupuesto (escenario activo) con el histórico
// real del banco del mismo rango de meses, ambos ya disponibles vía la API.
export default function BudgetVsActual() {
  const [budgets, setBudgets] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [scenario, setScenario] = useState("medium");
  const [budget, setBudget] = useState(null);
  const [actuals, setActuals] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Cargar la lista de presupuestos y preseleccionar el primero.
  useEffect(() => {
    listBudgets().then((list) => {
      setBudgets(list);
      if (list.length) setSelectedId((prev) => prev ?? list[0].id);
    });
  }, []);

  // Al elegir presupuesto: cargar su detalle (con escenarios) y el real del mismo rango.
  useEffect(() => {
    if (!selectedId) return;
    let alive = true;
    setLoading(true); setError(null);
    getBudget(selectedId)
      .then(async (b) => {
        if (!alive) return;
        setBudget(b);
        // El real solo existe hasta el mes actual: no pedimos meses futuros.
        const curM = currentYM();
        const actEnd = b.end_month > curM ? curM : b.end_month;
        const act = b.start_month <= actEnd ? await getActuals(b.start_month, actEnd) : null;
        if (alive) setActuals(act);
      })
      .catch(() => { if (alive) setError("No se pudo cargar la comparación."); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [selectedId]);

  const budScenario = budget?.computed?.scenarios?.[scenario];
  const actScenario = actuals?.computed?.scenarios?.medium;

  // Cruce mes a mes. Para meses posteriores al actual no hay real todavía.
  const cur = currentYM();
  const rows = (() => {
    if (!budScenario?.months?.length) return [];
    const actByMonth = Object.fromEntries((actScenario?.months ?? []).map((m) => [m.month, m]));
    return budScenario.months.map((bm) => {
      const isFuture = bm.month > cur;
      const am = !isFuture ? actByMonth[bm.month] : null;
      const budResult = bm.result ?? 0;
      const actResult = am ? (am.result ?? 0) : null;
      const dev = actResult == null ? null : actResult - budResult;
      const devPct = actResult == null || Math.abs(budResult) < 0.005
        ? null
        : (dev / Math.abs(budResult)) * 100;
      return {
        month: bm.month,
        isFuture,
        budResult,
        actResult,
        dev,
        devPct,
        budCash: bm.cash ?? null,
        actCash: am ? (am.cash ?? null) : null,
      };
    });
  })();

  const realRows = rows.filter((r) => r.actResult != null);
  const sumBudReal = realRows.reduce((a, r) => a + r.budResult, 0); // presupuestado de los meses con real
  const sumActReal = realRows.reduce((a, r) => a + (r.actResult ?? 0), 0);
  const lastReal = realRows[realRows.length - 1] ?? null;

  // Datos del gráfico: caja presupuestada (toda) vs caja real (solo meses con real).
  const chartData = rows.map((r) => ({
    month: monthLabel(r.month),
    Presupuestado: r.budCash,
    Real: r.actCash,
  }));

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      {/* Selectores */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <label className="text-sm text-gray-600 flex items-center gap-2">
          Presupuesto:
          <select
            className="border border-slate-200 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-slate-900"
            value={selectedId ?? ""}
            onChange={(e) => setSelectedId(e.target.value)}
          >
            {budgets.length === 0 && <option value="">— sin presupuestos —</option>}
            {budgets.map((b) => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
        </label>

        <div className="inline-flex rounded-lg border border-slate-200 p-0.5 bg-slate-50">
          {SCENARIOS.map((s) => (
            <button
              key={s.value}
              onClick={() => setScenario(s.value)}
              className={`px-3 py-1 text-sm rounded-md transition-colors ${
                scenario === s.value ? "bg-slate-800 text-white" : "text-slate-600 hover:text-slate-900"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {budgets.length === 0 && !loading && (
        <p className="text-gray-400">Aún no tienes presupuestos. Crea uno para poder compararlo con el real.</p>
      )}
      {error && <p className="text-red-600 text-sm mb-4">{error}</p>}
      {loading && <p className="text-gray-400 mb-4">Cargando…</p>}

      {!loading && budget && rows.length > 0 && (
        <>
          {/* KPIs de desvío */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
            <KpiDev
              label="Resultado acumulado (meses con real)"
              real={sumActReal}
              bud={sumBudReal}
            />
            <KpiDev
              label={lastReal ? `En el banco (${monthLabel(lastReal.month)})` : "En el banco"}
              real={lastReal?.actCash ?? null}
              bud={lastReal?.budCash ?? null}
            />
            <div className="border rounded-lg p-3 bg-white">
              <div className="text-xs text-gray-500">Meses comparados</div>
              <div className="text-lg font-semibold text-slate-800">
                {realRows.length} <span className="text-sm text-gray-400">de {rows.length}</span>
              </div>
              <div className="text-xs text-gray-400">Los meses futuros aún no tienen real.</div>
            </div>
          </div>

          {/* Gráfico: caja presupuestada vs real */}
          <div className="border rounded-lg p-4 bg-white mb-6">
            <div className="text-sm font-semibold mb-2">En el banco: presupuestado vs real</div>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" fontSize={11} />
                <YAxis fontSize={11} />
                <Tooltip formatter={(v) => (v == null ? "—" : eur(v))} />
                <Legend />
                <Line
                  type="monotone" dataKey="Presupuestado" stroke="#94a3b8"
                  strokeDasharray="5 4" dot={false} strokeWidth={2}
                />
                <Line
                  type="monotone" dataKey="Real" stroke="#2563eb"
                  dot={false} strokeWidth={2.5} connectNulls={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Tabla mes a mes del resultado */}
          <div className="border rounded-lg bg-white overflow-x-auto mb-8">
            <table className="text-sm border-collapse min-w-full">
              <thead>
                <tr className="text-gray-500 border-b bg-slate-50">
                  <th className="sticky left-0 z-10 bg-slate-50 text-left font-medium px-3 py-2 min-w-[110px]">Mes</th>
                  <th className="text-right font-medium px-3 py-2 min-w-[120px]">Presupuestado</th>
                  <th className="text-right font-medium px-3 py-2 min-w-[120px]">Real</th>
                  <th className="text-right font-medium px-3 py-2 min-w-[120px]">Desvío</th>
                  <th className="text-right font-medium px-3 py-2 min-w-[90px]">Desvío %</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.month} className="border-b last:border-0 hover:bg-slate-50">
                    <td className="sticky left-0 z-10 bg-white px-3 py-2 whitespace-nowrap">{monthLabel(r.month)}</td>
                    <td className="text-right px-3 py-2 tabular-nums text-slate-600">{eur(r.budResult)}</td>
                    <td className="text-right px-3 py-2 tabular-nums font-medium text-slate-800">
                      {r.actResult == null ? <span className="text-slate-300">—</span> : eur(r.actResult)}
                    </td>
                    <td className={`text-right px-3 py-2 tabular-nums font-medium ${devTone(r.actResult, r.budResult)}`}>
                      {r.dev == null ? <span className="text-slate-300">—</span> : `${r.dev > 0 ? "+" : ""}${eur(r.dev)}`}
                    </td>
                    <td className={`text-right px-3 py-2 tabular-nums ${devTone(r.actResult, r.budResult)}`}>
                      {r.devPct == null ? <span className="text-slate-300">—</span> : `${r.devPct > 0 ? "+" : ""}${r.devPct.toFixed(0)}%`}
                    </td>
                  </tr>
                ))}
                {/* Totales de los meses con real */}
                <tr className="border-t-2 border-slate-300 font-bold bg-slate-100">
                  <td className="sticky left-0 z-10 bg-slate-100 px-3 py-2.5">Acumulado (real)</td>
                  <td className="text-right px-3 py-2.5 tabular-nums text-slate-600">{eur(sumBudReal)}</td>
                  <td className="text-right px-3 py-2.5 tabular-nums text-slate-800">{eur(sumActReal)}</td>
                  <td className={`text-right px-3 py-2.5 tabular-nums ${devTone(sumActReal, sumBudReal)}`}>
                    {`${sumActReal - sumBudReal > 0 ? "+" : ""}${eur(sumActReal - sumBudReal)}`}
                  </td>
                  <td className="text-right px-3 py-2.5" />
                </tr>
              </tbody>
            </table>
          </div>

          <p className="text-xs text-gray-400">
            Comparación del <strong>resultado mensual</strong> (lo que entra menos lo que sale) y de la{" "}
            <strong>caja</strong> del escenario <strong>{SCENARIOS.find((s) => s.value === scenario)?.label}</strong>{" "}
            frente al movimiento real del banco. El desvío en verde = vas mejor de lo previsto; en rojo = por debajo.
          </p>
        </>
      )}
    </div>
  );
}

function KpiDev({ label, real, bud }) {
  const dev = real == null || bud == null ? null : real - bud;
  return (
    <div className="border rounded-lg p-3 bg-white">
      <div className="text-xs text-gray-500">{label}</div>
      <div className={`text-lg font-semibold ${real == null ? "text-slate-400" : "text-slate-800"}`}>
        {real == null ? "—" : eur(real)}
      </div>
      <div className="text-xs text-gray-400">
        Presupuestado: {bud == null ? "—" : eur(bud)}
        {dev != null && (
          <span className={`ml-1 font-medium ${devTone(real, bud)}`}>
            ({dev > 0 ? "+" : ""}{eur(dev)})
          </span>
        )}
      </div>
    </div>
  );
}
